import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { CheckoutActions } from "../actions";
import { Observable, catchError, forkJoin, map, of, retry, switchMap, timer, filter, take, takeUntil } from "rxjs";
import { AccordionType, CheckoutCartDetails, CheckoutCartsErrorResponse } from "../../models/checkout.model";
import { ApiService } from "../../../../SGRE-shared/services/api.service";
import { CheckoutCartsEffectsService } from "./checkout-carts-effects.service";
import { CheckoutAccordion, CheckoutCartsState } from "../checkout.state";
import { StorageService } from "../../../../SGRE-shared/services/storage.service";
import { UpdateCartProductRequest } from "../../../cart/models/cartData";
import { CheckoutService } from "../../services/checkout.service";

@Injectable()
export class CheckoutCartsEffects {

    constructor(
        private actions$: Actions,
        private apiService: ApiService,
        private checkoutCartsEffectsService: CheckoutCartsEffectsService,
        private storageService: StorageService,
        private checkoutService: CheckoutService,
    ) { }

    checkoutCartsList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CheckoutActions.loadCheckoutCartsList),
            switchMap(({ payload, legalEntityId }) => {
                let paramsObject = {
                    fields: 'FULL',
                    legalEntityId
                };

                // Create observables for the API calls based on the availability of cart IDs
                const warehouseCartRequest$: Observable<CheckoutCartDetails | CheckoutCartsErrorResponse> =
                    (payload?.CheckoutCartIds?.warehouseCartId)
                        ? this.apiService.getCartsList(payload.CheckoutCartIds.warehouseCartId, paramsObject)
                            .pipe(
                                map((data: unknown) => data as CheckoutCartDetails),
                                catchError(error => of({ error, type: AccordionType.WAREHOUSE } as CheckoutCartsErrorResponse))
                            )
                        : of<CheckoutCartDetails | CheckoutCartsErrorResponse>(null);

                const quotationCartRequest$: Observable<CheckoutCartDetails | CheckoutCartsErrorResponse> =
                    (payload?.CheckoutCartIds?.quotationCartId)
                        ? this.apiService.getCartsList(payload.CheckoutCartIds.quotationCartId, paramsObject)
                            .pipe(
                                map((data: unknown) => data as CheckoutCartDetails),
                                catchError(error => of({ error, type: AccordionType.QUOTATION } as CheckoutCartsErrorResponse))
                            )
                        : of<CheckoutCartDetails | CheckoutCartsErrorResponse>(null);

                return forkJoin([
                    warehouseCartRequest$,
                    quotationCartRequest$
                ]).pipe(
                    takeUntil(this.actions$.pipe(ofType(CheckoutActions.clearCheckoutState))),
                    map(([warehouseResponse, quotationResponse]) => {
                        return this.checkoutCartsEffectsService.modifyCheckoutCartsResponse([warehouseResponse, quotationResponse], payload.orderType);
                    })
                );
            }),
            map((modifiedResponse: CheckoutCartsState) => {
                if (modifiedResponse.error) {
                    return CheckoutActions.loadCheckoutCartsListFail({ payload: modifiedResponse });
                }
                return CheckoutActions.loadCheckoutCartsListSuccess({ payload: modifiedResponse });
            })
        )
    );

    checkoutCart$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CheckoutActions.loadCheckoutCart),
            switchMap(({ cartId, legalEntityId, accordionType, orderType }) => {
                let paramsObject = {
                    fields: 'FULL',
                    legalEntityId
                };
                return this.apiService.getCartsList(cartId, paramsObject)
                    .pipe(
                        takeUntil(this.actions$.pipe(ofType(CheckoutActions.clearCheckoutState))),
                        map((data: unknown) => {
                            const typedData = data as CheckoutCartDetails;
                            let payload: CheckoutCartsState = this.checkoutCartsEffectsService.modifyCartData(typedData, orderType);
                            return CheckoutActions.loadCheckoutCartSuccess({ payload, accordionType });
                        }),
                        catchError(error => {
                            let errorData: CheckoutCartsErrorResponse = { error, type: accordionType };
                            let payload: CheckoutCartsState = this.checkoutCartsEffectsService.modifyCartData(errorData, orderType);
                            return of(CheckoutActions.loadCheckoutCartFail({ payload, accordionType }));
                        })
                    )
            })
        )
    );

    updateCheckoutCart$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CheckoutActions.updateCheckoutCart),
            switchMap(({ cartId, rowIndex, quantityVal, legalEntityId, accordionType, orderType }) => {
                let qtyPayload: UpdateCartProductRequest = { quantity: quantityVal };
                return this.apiService.updateProductsInCart(cartId, rowIndex, qtyPayload)
                    .pipe(
                        retry({
                            count: 3,
                            delay: (error, retryAttempt) => {
                                if (error.status === 500 && error.error.code === 'DEADLOCK_VICTIM') {
                                    console.error(
                                        `Deadlock occurred on attempt ${retryAttempt}. Retrying in 1 second...`
                                    );
                                }
                                // Return an Observable that delays the next retry
                                return timer(1000);
                            }
                        }),
                        takeUntil(this.actions$.pipe(ofType(CheckoutActions.clearCheckoutState))),
                        map(() => CheckoutActions.loadCheckoutCart({ cartId, legalEntityId, accordionType, orderType })),
                        catchError(error => {
                            let errorData: CheckoutCartsErrorResponse = { error, type: accordionType };
                            let payload: CheckoutCartsState = this.checkoutCartsEffectsService.modifyCartData(errorData, orderType);
                            return of(CheckoutActions.loadCheckoutCartFail({ payload, accordionType }));
                        })
                    );
            })
        )
    );

    deleteCheckoutCart$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CheckoutActions.deleteCheckoutCart),
            switchMap(({ cartId, rowIndex, legalEntityId, accordionType, orderType }) => {
                return this.apiService.deleteCart(cartId, rowIndex)
                    .pipe(
                        takeUntil(this.actions$.pipe(ofType(CheckoutActions.clearCheckoutState))),
                        map(() => {
                            // Delete the cartId from browserStorage if it is the last entry
                            this.checkoutService.getCheckoutCartDetails()
                                .pipe(
                                    filter(data => data?.length > 0),
                                    take(1)
                                )
                                .subscribe((data: CheckoutAccordion[]) => {
                                    if (data.some(item => item.type === accordionType && item?.tableData?.length === 1)) {
                                        (accordionType === AccordionType.WAREHOUSE)
                                            ? this.storageService.cartCode = null
                                            : this.storageService.secondaryCartCode = null;
                                    }
                                });
                            return CheckoutActions.loadCheckoutCart({ cartId, legalEntityId, accordionType, orderType })
                        }),
                        catchError(error => {
                            let errorData: CheckoutCartsErrorResponse = { error, type: accordionType };
                            let payload: CheckoutCartsState = this.checkoutCartsEffectsService.modifyCartData(errorData, orderType);
                            return of(CheckoutActions.loadCheckoutCartFail({ payload, accordionType }));
                        })
                    )
            })
        )
    );

    removeAllParts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CheckoutActions.removeAllParts),
            switchMap(({ cartId, cartGroupId, accordionType, orderType }) =>
                this.apiService.removeCart(cartGroupId, cartId)
                    .pipe(
                        takeUntil(this.actions$.pipe(ofType(CheckoutActions.clearCheckoutState))),
                        map(() => {
                            (accordionType === AccordionType.WAREHOUSE)
                                ? this.storageService.cartCode = null
                                : this.storageService.secondaryCartCode = null;
                            return CheckoutActions.removeAllPartsSuccess({ accordionType });
                        }),
                        catchError(error => {
                            let errorData: CheckoutCartsErrorResponse = { error, type: accordionType };
                            let payload: CheckoutCartsState = this.checkoutCartsEffectsService.modifyCartData(errorData, orderType);
                            return of(CheckoutActions.loadCheckoutCartFail({ payload, accordionType }));
                        })
                    )
            )
        )
    );
}